// Dialog — center modal with scrim and focus trap.

import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "@/lib/cn";

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title?: ReactNode;
  children?: ReactNode;
  footer?: ReactNode;
  width?: number | string;
}

export function Dialog({ open, onClose, title, children, footer, width = 480 }: DialogProps) {
  const panelRef = useRef<HTMLDivElement | null>(null);
  const restoreRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    if (!open) return;
    restoreRef.current = (document.activeElement as HTMLElement) ?? null;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") { e.stopPropagation(); onClose(); } };
    document.addEventListener("keydown", onKey);
    panelRef.current?.focus();
    const restore = restoreRef.current;
    return () => {
      document.removeEventListener("keydown", onKey);
      restore?.focus?.();
    };
  }, [open, onClose]);

  return (
    <div className={cn("lex-dialog", open && "lex-dialog--open")} aria-hidden={!open}>
      <div className="lex-dialog__scrim" onClick={onClose} aria-hidden="true" />
      <div
        ref={panelRef}
        className="lex-dialog__panel"
        role="dialog"
        aria-modal="true"
        aria-label={typeof title === "string" ? title : "Dialog"}
        tabIndex={-1}
        style={{ width: typeof width === "number" ? `${width}px` : width, maxWidth: "92vw" }}
      >
        {title != null ? <header className="lex-dialog__header"><h2>{title}</h2></header> : null}
        {children != null ? <div className="lex-dialog__body">{children}</div> : null}
        {footer != null ? <footer className="lex-dialog__footer">{footer}</footer> : null}
      </div>
    </div>
  );
}

export default Dialog;
